import { useEffect, useMemo, useState } from "react";
import { Cake, X } from "lucide-react";
import api from "@/lib/api";
import { localDateISO } from "@/lib/utils";

const CONFETTI_COLORS = ["#C9A227", "#E8C766", "#1B2A4A", "#F4E9DC", "#B23A48", "#7BA05B"];

function Confetti() {
  const pieces = useMemo(() => {
    const count = typeof window !== "undefined" && window.innerWidth < 640 ? 26 : 44;
    return Array.from({ length: count }, (_, i) => ({
      key: crypto.randomUUID(),
      left: Math.random() * 100,
      delay: Math.random() * 0.6,
      duration: 2.6 + Math.random() * 1.6,
      size: 6 + Math.random() * 6,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      drift: (Math.random() - 0.5) * 16,
      round: i % 3 === 0,
      strip: i % 3 === 1,
    }));
  }, []);
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-40 overflow-hidden" data-testid="birthday-confetti">
      {pieces.map((p) => (
        <span
          key={p.key}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            width: p.strip ? p.size / 2.5 : p.size,
            height: p.strip ? p.size * 1.8 : p.size,
            backgroundColor: p.color,
            borderRadius: p.round ? "50%" : "1px",
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--confetti-drift": `${p.drift}vw`,
          }}
        />
      ))}
    </div>
  );
}

export default function BirthdayBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const todayKey = `birthday-dismissed-${localDateISO()}`;

  useEffect(() => {
    api
      .get("/public/birthdays")
      .then((r) => setData(r.data))
      .catch(() => {
        // Birthday banner is non-critical; fail silently on the public site.
      });
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem(todayKey)) setDismissed(true);
  }, [todayKey]);

  useEffect(() => {
    if (data?.enabled && data.display_duration > 0 && data.members?.length) {
      const t = setTimeout(() => setDismissed(true), data.display_duration * 1000);
      return () => clearTimeout(t);
    }
  }, [data]);

  const [showConfetti, setShowConfetti] = useState(false);
  useEffect(() => {
    const visible = Boolean(data?.enabled && data.members?.length && !dismissed);
    if (!visible || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShowConfetti(false);
      return;
    }
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(t);
  }, [data, dismissed]);

  if (!data?.enabled || !data.members?.length || dismissed) return null;

  const members = data.members;

  const DEFAULT_WISH =
    "May God bless you abundantly and guide you throughout the coming year. We wish you happiness, good health, peace and many more blessed years.";

  // The greeting name ALWAYS comes from the record currently being rendered.
  // If the saved template is missing the [Name] token (e.g. a real name was
  // typed into settings by mistake), we generate a safe greeting from THIS
  // record's name instead of leaking whatever name the template contains.
  const messageFor = (m) => {
    const tpl = data.message || "";
    if (!/\[Name\]/i.test(tpl)) {
      return `Happy Birthday, ${m.name}! 🎂\n${DEFAULT_WISH}`;
    }
    let msg = tpl.replace(/\[Name\]/gi, m.name);
    if (data.show_age && m.age != null) {
      msg = msg.replace(/\[Age\]/gi, String(m.age));
    } else {
      msg = msg.replace(/\s*\[Age\]/gi, "");
    }
    return msg;
  };

  const heading = members.length > 1 ? "Today's Birthdays" : "Happy Birthday!";

  const close = () => {
    sessionStorage.setItem(todayKey, "1");
    setDismissed(true);
  };

  if (data.style === "card") {
    return (
      <>
        {showConfetti && <Confetti />}
        <div
          className="fixed bottom-4 right-4 z-50 flex max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-sm flex-col gap-3 overflow-y-auto"
          data-testid="birthday-banner"
        >
          {members.map((m, i) => (
            <div
              key={m.id || `${m.name}-${i}`}
              className="relative animate-fade-up rounded-lg border border-gold/50 bg-navy p-5 shadow-2xl grain-overlay"
              data-testid={`birthday-ribbon-${i}`}
              role="status"
            >
              <button onClick={close} aria-label="Close birthday message" data-testid={`birthday-banner-close-${i}`}
                className="absolute right-3 top-3 rounded-full p-1 text-cream/60 hover:bg-cream/10 hover:text-cream transition-colors">
                <X className="h-4 w-4" />
              </button>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/20 text-gold">
                  <Cake className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-heading text-base font-bold text-gold">{heading}</p>
                  <p className="mt-0.5 text-sm font-semibold text-cream" data-testid={`birthday-name-${i}`}>{m.name}</p>
                  <p className="mt-1 text-xs leading-relaxed text-cream/70 whitespace-pre-line" data-testid={`birthday-message-${i}`}>{messageFor(m)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {showConfetti && <Confetti />}
      <div data-testid="birthday-banner">
        {members.map((m, i) => (
          <section
            key={m.id || `${m.name}-${i}`}
            className="relative overflow-hidden border-b border-gold/30 bg-navy grain-overlay animate-fade-up"
            data-testid={`birthday-ribbon-${i}`}
            role="status"
          >
            <div className="mx-auto flex max-w-7xl items-start gap-4 px-5 py-5 sm:px-8">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/20 text-gold">
                <Cake className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-lg font-bold text-gold">{heading}</p>
                <p className="mt-0.5 text-sm font-semibold text-cream" data-testid={`birthday-name-${i}`}>{m.name}</p>
                <p className="mt-1 text-xs leading-relaxed text-cream/70 whitespace-pre-line" data-testid={`birthday-message-${i}`}>{messageFor(m)}</p>
              </div>
              <button onClick={close} aria-label="Close birthday message" data-testid={`birthday-banner-close-${i}`}
                className="rounded-full p-1.5 text-cream/60 hover:bg-cream/10 hover:text-cream transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
